import React from 'react';
import { MarketActivityResponse } from '../../api/metrics';
import { AlertTriangleIcon, CheckCircleIcon, ScaleIcon } from '../shared/Icons';

interface DailyFlowSummaryProps {
  data: MarketActivityResponse | undefined;
  isLoading: boolean;
  onToggleBreakdown: () => void;
  showBreakdown: boolean;
}

const DailyFlowSummary: React.FC<DailyFlowSummaryProps> = ({ data, isLoading, onToggleBreakdown, showBreakdown }) => {
  if (isLoading) {
    return (
      <div className="daily-flow-summary loading">
        <div className="chart-loading">
          <div className="loading-spinner"></div>
          <p>Loading daily flow summary...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="daily-flow-summary empty">
        <p>No trading activity today</p>
      </div>
    );
  }

  const { summary } = data;
  const totalVolume = summary.totalBuyVolume + summary.totalSellVolume;
  const buyPercent = totalVolume > 0 ? (summary.totalBuyVolume / totalVolume) * 100 : 0;
  const sellPercent = totalVolume > 0 ? (summary.totalSellVolume / totalVolume) * 100 : 0;

  // Determine trend icon and styling
  const getTrendInfo = () => {
    switch (summary.marketTrend) {
      case 'DOWN':
        return {
          icon: <AlertTriangleIcon size={28} />,
          text: 'Market Trending Down',
          className: 'trend-down',
          description: 'More buys than sells (buying the dip)'
        };
      case 'UP':
        return {
          icon: <CheckCircleIcon size={28} />,
          text: 'Market Trending Up',
          className: 'trend-up',
          description: 'More sells than buys (taking profits)'
        };
      default:
        return {
          icon: <ScaleIcon size={28} />,
          text: 'Balanced Market',
          className: 'trend-balanced',
          description: 'Buy and sell volumes are balanced'
        };
    }
  };

  const trendInfo = getTrendInfo();

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatRatio = (ratio: number) => {
    if (ratio > 100) return '99.9x';
    return `${ratio.toFixed(1)}x`;
  };

  return (
    <div className="daily-flow-summary">
      {/* Removed redundant header - now handled by parent component */}

      {/* Flow Bar Visualization */}
      <div className="flow-bar-container">
        <div className="flow-bar">
          <div
            className="flow-bar-buy"
            style={{ width: `${buyPercent}%` }}
            title={`${buyPercent.toFixed(1)}% buys`}
          ></div>
          <div
            className="flow-bar-sell"
            style={{ width: `${sellPercent}%` }}
            title={`${sellPercent.toFixed(1)}% sells`}
          ></div>
        </div>
        <div className="flow-bar-labels">
          <div className="flow-label flow-label-buy">
            <span className="flow-amount">{formatCurrency(summary.totalBuyVolume)}</span>
            <span className="flow-percent">Buys ({buyPercent.toFixed(0)}%)</span>
          </div>
          <div className="flow-label flow-label-sell">
            <span className="flow-amount">{formatCurrency(summary.totalSellVolume)}</span>
            <span className="flow-percent">Sells ({sellPercent.toFixed(0)}%)</span>
          </div>
        </div>
      </div>

      {/* Summary Metrics */}
      <div className="summary-metrics">
        <div className={`metric-card trend-card ${trendInfo.className}`}>
          <div className="metric-icon">{trendInfo.icon}</div>
          <div className="metric-content">
            <div className="metric-label">{trendInfo.text}</div>
            <div className="metric-description">{trendInfo.description}</div>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-label">Net Flow</div>
          <div className={`metric-value ${summary.netFlow < 0 ? 'negative' : 'positive'}`}>
            {summary.netFlow > 0 ? '+' : ''}{formatCurrency(summary.netFlow)}
            <span className="metric-sublabel">
              {summary.netFlow < 0 ? ' deployed' : ' captured'}
            </span>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-label">Buy/Sell Ratio</div>
          <div className="metric-value">
            {formatRatio(summary.buyToSellRatio)}
            <span className="metric-sublabel">
              {summary.buyToSellRatio > 1 ? ' more buys' : ' more sells'}
            </span>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-label">Order Count</div>
          <div className="metric-value">
            {summary.buyOrderCount} buy vs {summary.sellOrderCount}{ ' sell' }
          </div>
        </div>
      </div>

      {/* Toggle Asset Breakdown Button */}
      <button className="toggle-breakdown-btn" onClick={onToggleBreakdown}>
        {showBreakdown ? '▼' : '▶'} Asset Breakdown ({data.assetBreakdown.length} assets)
      </button>
    </div>
  );
};

export default DailyFlowSummary;
